import { computed, ref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { DateTime, Interval } from 'luxon';

import { Timetable } from '@/shared/interfaces/timeTables';
import { TrackActivity } from '@/shared/interfaces/timeTables';
import { anykrowdApi } from '@/api/anykrowdApi';
import useStorageService from '@/shared/composables/useStorageService';

const { get, set } = useStorageService();

// Get duration data
const calculateDurationData = (activity: TrackActivity, timelineDetails: (DateTime | null)[], quarterHourWidth = 40) => {
	// This uses a table system
	// - Each hour is divided in 4 cells
	// - Each cell uses the provided quarter-hour width
	// - Each cell represents 15 minutes
	const safeQuarterHourWidth = quarterHourWidth > 0 ? quarterHourWidth : 40;
	const pixelsPerMinute = safeQuarterHourWidth / 15;

	// Get the timeline start (first hour)
	const timelineStart = timelineDetails[0];
	if (!timelineStart) {
		console.error('[Timetable] Timeline has no start time');
		return { duration: '', startFineOffset: '' };
	}

	// Calculate hours from timeline start to activity start
	// This works correctly across day/month boundaries
	const hoursFromStart = activity.startAt.diff(timelineStart, 'hours').hours;

	// Calculate the duration expressed in pixels
	const interval = Interval.fromDateTimes(activity.startAt, activity.endAt);
	const durationInPixels = (interval.toDuration().milliseconds / 1000 / 60) * pixelsPerMinute;

	// Calculate fine offset for start (within the hour cell)
	// We need fine grain positioning for activities starting at non-hour times like 16:20
	const minuteInteger = Math.trunc(activity.startAt.minute / 15);
	const minuteDecimal = activity.startAt.minute / 15;
	const startFineOffset = `margin-left:${Math.trunc((minuteDecimal - minuteInteger) * safeQuarterHourWidth) + Math.round(safeQuarterHourWidth / 2)}px`;

	// Calculate grid column start:
	// - hoursFromStart * 4 (4 cells per hour)
	// - + 2 (grid starts at column 2, column 1 is for track title)
	// - + minuteInteger (additional cells for minutes within the hour)
	const activityStart = Math.floor(hoursFromStart) * 4 + 2 + Math.trunc(activity.startAt.minute / 15);

	return {
		duration: `grid-column-start:${activityStart};width:${durationInPixels}px;`,
		startFineOffset,
	};
};

// Calculate progress percent
const calculateProgressPercent = (activity: TrackActivity) => {
	// Init
	const now = DateTime.now();
	const interval = Interval.fromDateTimes(activity.startAt, activity.endAt);

	// Interval's end date is before than now
	// Activity already ended
	if (interval.isBefore(now)) {
		return 1;
	}

	// Interval's start date is after than now
	// Activity has not started yet
	if (interval.isAfter(now)) {
		return 0;
	}

	// Total duration of activity
	const totalActivityDuration = interval.length('milliseconds');

	// Duration from start to now
	const currentDuration = Interval.fromDateTimes(activity.startAt, now).length('milliseconds');

	// Calculate percentage
	const currentDurationPercentage = currentDuration / totalActivityDuration;

	return currentDurationPercentage;
};

/**
 * Calculate the size of data in KB
 */
const calculateDataSize = (data: any): number => {
	try {
		return new Blob([JSON.stringify(data)]).size / 1024;
	} catch {
		return 0;
	}
};

/**
 * Optimize timetable data for storage by removing large/redundant fields
 * This reduces storage size by ~60-70%
 */
const optimizeTimeTableForStorage = (timeTable: any): any => {
	return {
		id: timeTable.id,
		name: timeTable.name,
		events: timeTable.events,
		tracks: timeTable.tracks.map((track: any) => ({
			id: track.id,
			name: track.name,
			// Keep icon/image URLs as they're small
			icon_url: track.icon_url,
			image_url: track.image_url,
			activities: track.activities.map((activity: any) => ({
				id: activity.id,
				pivot_id: activity.pivot_id,
				name: activity.name,
				description: activity.description,
				description_short: activity.description_short,
				is_favorite: activity.is_favorite,
				start_at: activity.start_at,
				end_at: activity.end_at,
				background_color: activity.background_color,
				background_color_opacity: activity.background_color_opacity,
				// Only keep URLs if they're reasonably sized
				...(activity.icon_url && activity.icon_url.length < 200 && { icon_url: activity.icon_url }),
				...(activity.image_url && activity.image_url.length < 200 && { image_url: activity.image_url }),
			})),
		})),
		// Note: timelineDetails, timelineDayBreakpoints are computed from start/end dates
		// so we don't need to store them - they'll be recalculated when Timetable class is instantiated
	};
};

// Get Timetable api call
const getTimeTableApiCall = async (timetableId: number, userId?: number | undefined): Promise<Timetable> => {
	const { data } = await anykrowdApi.get<Timetable>(`/timetables/${timetableId}?user_id=${userId}`, false);
	return data;
};

// Get Timetable
const getTimeTable = (timeTableId: number, userId: number | undefined) => {
	// Init
	const timeTable = ref<Timetable>()!;

	const {
		data: timeTableData,
		isFetched,
		refetch: refetchTimeTable,
		remove: removeTimeTableDataFromCache,
	} = useQuery({
		queryKey: ['timeTable', timeTableId],
		queryFn: () => getTimeTableApiCall(timeTableId, userId),
		retry: 3,
	});

	// Watch timeTableData
	watch(
		timeTableData,
		() => {
			if (timeTableData.value) {
				timeTable.value = new Timetable(timeTableData.value);

				// Update cache with optimized data
				// Remove large/computed fields to reduce storage size
				const optimizedData = optimizeTimeTableForStorage(timeTableData.value);
				const dataSize = calculateDataSize(optimizedData);
				const MAX_TIMETABLE_SIZE_KB = 300; // Don't cache timetables larger than 300KB

				if (dataSize > MAX_TIMETABLE_SIZE_KB) {
					console.warn(`[TimeTables] Skipping cache for timetable ${timeTableId} - too large (${dataSize.toFixed(2)}KB > ${MAX_TIMETABLE_SIZE_KB}KB). ` + `Timetable will be fetched from API on next access.`);
				} else {
					const key = ['timeTable', timeTableId].join('-');
					set(key, optimizedData);
				}
			}
		},
		{ immediate: true },
	);

	return {
		timeTable,
		isFetchedTimeTable: computed(() => isFetched.value),
		refetchTimeTable,
		removeTimeTableDataFromCache,
	};
};

// getTimetablesApiCall
const getTimetablesApiCall = async (): Promise<Timetable[]> => {
	const { data } = await anykrowdApi.get<any>('/timetables', false);
	return data.data;
};

// Get Timetable
const getTimeTables = () => {
	// Init
	const timeTables = ref<Timetable[]>()!;

	const {
		data: timeTablesData,
		isFetched,
		refetch: refetchTimeTables,
		remove: removeTimeTablesDataFromCache,
	} = useQuery({
		queryKey: ['timeTables'],
		queryFn: () => getTimetablesApiCall(),
		retry: 3,
	});

	// Watch timeTablesData
	watch(
		timeTablesData,
		() => {
			if (timeTablesData.value) {
				timeTables.value = [...timeTablesData.value.map((timeTable) => new Timetable(timeTable))];

				// Update cache with optimized data
				const optimizedData = timeTablesData.value.map((timeTable) => optimizeTimeTableForStorage(timeTable));
				const dataSize = calculateDataSize(optimizedData);
				const MAX_TIMETABLES_LIST_SIZE_KB = 200;

				if (dataSize > MAX_TIMETABLES_LIST_SIZE_KB) {
					console.warn(`[TimeTables] Skipping cache for timetables list - too large (${dataSize.toFixed(2)}KB > ${MAX_TIMETABLES_LIST_SIZE_KB}KB)`);
				} else {
					set('timeTables', optimizedData);
				}
			}
		},
		{ immediate: true },
	);

	return {
		timeTables,
		isFetchedTimeTables: computed(() => isFetched.value),
		refetchTimeTables,
		removeTimeTablesDataFromCache,
	};
};

const scrollToNextDay = (nextDay: DateTime) => {
	document.getElementById(`day-${nextDay.day}`)!.scrollIntoView({
		behavior: 'smooth',
		inline: 'start',
	});
};

const scrollToPreviousDay = (previousDay: DateTime) => {
	document.getElementById(`day-${previousDay.day}`)!.scrollIntoView({
		behavior: 'smooth',
		inline: 'start',
	});
};

const useTimeTables = () => {
	return {
		calculateDurationData,
		calculateProgressPercent,
		getTimeTable,
		getTimeTableApiCall,
		getTimeTables,
		getTimetablesApiCall,
		scrollToNextDay,
		scrollToPreviousDay,
	};
};

export default useTimeTables;
